import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import FalconCloseButton from "components/common/FalconCloseButton";
import MultiSelect from "components/common/MultiSelect";
import { nationalityData } from "features/practitioner/utils";
import React, { useEffect, useRef } from "react";
import { Form, Modal, Row, Col, Button } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { GetAllDropDownData } from "features/eliglibility/eliglibility-check/config/eligibilityCheckApi";

const EditEmployeeModal = ({
  show,
  onHide,
  rowData,
  setSelectedRow,
  updateRowData,
}) => {
  const dispatch = useDispatch();
  const { systemPrivillageList } = useSelector((state) => state.empolyee);
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const prevRowNum = useRef(null);
  const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];
  const getValueFromTitle = (dataList, title) => {
    if (!title) return "";
    const normalized = title?.trim().toLowerCase();
    const match = dataList.find(
      (item) => item.title?.trim().toLowerCase() === normalized
    );
    return match ? match.value : title;
  };

  useEffect(() => {
    if (systemPrivillageList?.length === 0) {
      dispatch(GetAllDropDownData());
    }
  }, [systemPrivillageList]);

  // ✅ Fill form when `rowData` is available
  useEffect(() => {
    if (show && rowData) {
      const nationalityValue = getValueFromTitle(
        nationalityData,
        rowData["Nationality"]
      );

      setValue(
        "first_name_eng",
        rowData["First Name (English)"] || rowData?.first_name_eng || ""
      );
      setValue(
        "last_name_eng",
        rowData["Family Name (English)"] || rowData?.last_name_eng || ""
      );
      setValue(
        "first_name_arabic",
        rowData["First Name (Arabic)"] || rowData?.first_name_arabic || ""
      );
      setValue(
        "last_name_arabic",
        rowData["Family Name (Arabic)"] || rowData?.last_name_arabic || ""
      );
      setValue("birth_date", rowData["DOB"] || rowData?.birth_date || "");
      setValue(
        "nationality",
        nationalityData?.find((item) =>
          item?.value === rowData?.nationality?.value
            ? rowData?.nationality?.value
            : nationalityValue || ""
        )
      );
      setValue("Mobile No", rowData["Mobile No"] || rowData?.mobile_no || "");
      setValue("email", rowData["Email"] || rowData?.email || "");
      setValue(
        "national_id",
        rowData["National Id"] || rowData?.national_id || ""
      );
      const branchValues =
        typeof rowData.Branch === "string"
          ? rowData.Branch.split(",")
              .map((branchIdStr) => {
                const trimmedId = branchIdStr.trim();
                const branch = allBranches.find(
                  (b) => b.branch_cchino === trimmedId
                );
                return branch
                  ? {
                      value: branch.branch_cchino,
                      label: branch.branch_name,
                      branch_cchino: branch.branch_cchino,
                    }
                  : null;
              })
              .filter(Boolean)
          : [];

      setValue(
        "Branch",
        Array.isArray(rowData.Branch) ? rowData.Branch : branchValues
      );
      setValue(
        "system_privilage",
        rowData?.["System Privilage"]
          ? systemPrivillageList?.find(
              (item) => item?.syspri_name == rowData?.["System Privilage"]
            )
          : rowData?.system_privilage || null
      );
      setValue("password", rowData["Password"] || rowData?.password || "");
    }
  }, [show, rowData, setValue]);

  const isEnglish = (v) => /^[A-Za-z0-9\s'-]+$/.test(v);
  const isArabic = (v) =>
    /^[\u0600-\u06FF\s\u064B-\u0652\u0670\u0671\u067E\u0686\u06A4\u06AF'-]+$/.test(
      v
    );

  const handleFormSubmit = (data) => {
    const formattedData = {
      ...data,
    };
    const isValid = Object.keys(errors).length === 0;

    // Update row data and validation status
    updateRowData(rowData.rowNum, formattedData, isValid);
    reset();
    onHide();
    setSelectedRow(null);
  };

  return (
    <Modal show={show} size="lg" centered>
      <Modal.Header>
        <Modal.Title>Edit Employee</Modal.Title>
        <FalconCloseButton
          onClick={() => {
            reset();
            onHide();
            setSelectedRow(null);
          }}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
          <Row>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="first_name_eng"
                label="First Name (English)"
                placeholder="First Name (English)"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                autoComplete="off"
                rules={{
                  required: "First name is required",
                  validate: (v) =>
                    isEnglish(v) || "Only English characters are allowed",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="last_name_eng"
                label="Family Name (English)"
                placeholder="Family Name (English)"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                autoComplete="off"
                rules={{
                  required: "Last name is required",
                  validate: (v) =>
                    isEnglish(v) || "Only English characters are allowed",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="first_name_arabic"
                label="First Name (Arabic)"
                placeholder="First Name (Arabic)"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                autoComplete="off"
                rules={{
                  validate: (v) =>
                    !v || isArabic(v) || "Must be Arabic characters",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="last_name_arabic"
                label="Family Name (Arabic)"
                placeholder="Family Name (Arabic)"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                autoComplete="off"
                rules={{
                  validate: (v) =>
                    !v || isArabic(v) || "Must be Arabic characters",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <BootDatePicker
                name="birth_date"
                label="DOB"
                size="sm"
                control={control}
                errors={errors}
                selected={watch("birth_date")}
                onChange={(date) => setValue("birth_date", date)}
                placeholder="YYYY-MM-DD"
                isRequired={false}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <label className="form-label mb-2">
                Nationality <span className="text-danger">*</span>
              </label>
              <Controller
                name="nationality"
                control={control}
                size="sm"
                rules={{ required: "Nationality is required" }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isMulti={false}
                    options={nationalityData?.map((item) => ({
                      value: item.value,
                      label: item.title,
                    }))}
                    isClearable={true}
                    value={
                      field.value
                        ? {
                            value: field.value.value,
                            label: field.value.title,
                          }
                        : null
                    }
                    onChange={(selected) =>
                      setValue(
                        "nationality",
                        selected
                          ? {
                              value: selected.value,
                              title: selected.label,
                            }
                          : null
                      )
                    }
                    placeholder="Choose ..."
                  />
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="Mobile No"
                label="Mobile No"
                control={control}
                size="sm"
                required={false}
                errors={errors}
                autoComplete="off"
                placeholder="05xxxxxxxx"
                type="tel"
                inputMode="numeric"
                rules={{
                  validate: (value) =>
                    !value ||
                    /^05\d{8}$/.test(value) ||
                    "Must start with 05 and be 10 digits",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="email"
                label="Email"
                placeholder="Email"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                autoComplete="off"
                rules={{
                  required: "Email is required",
                  validate: (val) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ||
                    "Invalid email address",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="national_id"
                label="National ID"
                placeholder="National ID"
                control={control}
                size="sm"
                errors={errors}
                required={true}
                rules={{
                  required: "National ID is required",
                  validate: (val) => {
                    const nationality = watch("nationality");
                    const id = String(val || "").trim();
                    if (!/^\d{10}$/.test(id))
                      return "National ID must be 10 digits";

                    if (nationality?.value === "SAU") {
                      return id.startsWith("1")
                        ? true
                        : "Saudi ID must start with 1";
                    } else {
                      return !id.startsWith("1")
                        ? true
                        : "Non-Saudi ID must not start with 1";
                    }
                  },
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <Form.Group>
                <label className="form-label">
                  Branch <span className="text-danger">*</span>
                </label>
                <Controller
                  name="Branch"
                  control={control}
                  size="sm"
                  rules={{
                    required: "At least one branch is required",
                    validate: (value) => value?.length > 0,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <MultiSelect
                        {...field}
                        closeMenuOnSelect={false}
                        isMulti
                        options={allBranches.map((branch) => ({
                          value: branch.branch_cchino,
                          label: branch.branch_name,
                          cchi: branch?.branch_cchino,
                        }))}
                        value={field.value}
                        onChange={(selected) => field.onChange(selected)}
                        placeholder="Choose branches..."
                      />
                      {error && (
                        <div className="text-danger small mt-1">
                          {error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <label className="form-label mb-2">
                System Privilage <span className="text-danger">*</span>
              </label>
              <Controller
                name="system_privilage"
                control={control}
                size="sm"
                rules={{
                  required: "System Privilage is required",
                  validate: (value) => {
                    if (!value) return "System Privilage is required.";
                    const allowed = ["Employee", "Admin", "Super Admin"];
                    if (!allowed.includes(value.syspri_name))
                      return "Invalid System Privilage.";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isMulti={false}
                    options={systemPrivillageList?.map((item) => ({
                      value: item.syspri_id,
                      label: item.syspri_name,
                    }))}
                    isClearable={true}
                    value={
                      field.value
                        ? {
                            value: field.value.syspri_id,
                            label: field.value.syspri_name,
                          }
                        : null
                    }
                    onChange={(selected) =>
                      setValue(
                        "system_privilage",
                        selected
                          ? {
                              syspri_id: selected.value,
                              syspri_name: selected.label,
                            }
                          : null
                      )
                    }
                    placeholder="Choose ..."
                  />
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={4} xl={3}>
              <TextInput
                name="password"
                control={control}
                size="sm"
                label="Password"
                placeholder="Password"
                type="password"
                errors={errors}
                autoComplete="new-password"
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long",
                  },
                }}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                reset();
                onHide();
                setSelectedRow(null);
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditEmployeeModal;
